import React, { useState } from 'react';
import gql from 'graphql-tag';
import { useStoreState } from 'easy-peasy';
import swal from 'sweetalert';
import { toast } from 'react-toastify';
import client from '../../../utils/apolloClient';
import PlaceSearchSelect from '../../../components/global/PlaceSearchSelect';

const updatePlaceMutation = gql`
  mutation updatePlace($input: PlaceInput) {
    update_place(input: $input) {
      place_id
      name
      error {
        code
        description
      }
    }
  }
`;

const CompanyDetails = ({ values, company }) => {
  const { userId } = useStoreState(state => state.auth);
  const { company_id } = values;

  const [selectedPlace, setSelectedPlace] = useState(null);
  const [linkedPlaces, setLinkedPlaces] = useState(company?.places || []);
  const [linking, setLinking] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState(null);
  const [resetKey, setResetKey] = useState(0);

  const handleLinkPlace = async () => {
    if (!selectedPlace) return;

    const alreadyLinked = linkedPlaces.some(
      p => p.place_id === selectedPlace.place_id,
    );

    if (alreadyLinked) {
      swal({
        title: 'Already Linked',
        text: `"${selectedPlace.name}" has already been linked to this company.`,
        icon: 'error',
      });
      return;
    }

    const confirmed = await swal({
      title: 'Link Place to Company',
      text: `Link "${selectedPlace.name}" to this company?`,
      icon: 'warning',
      buttons: ['Cancel', 'Yes, Link'],
    });

    if (!confirmed) return;

    try {
      setLinking(true);
      const { data } = await client.clientPrivate.mutate({
        mutation: updatePlaceMutation,
        variables: {
          input: { user_id: userId, place_id: selectedPlace.place_id, company_id },
        },
      });

      const errors = data?.update_place?.error;
      if (errors && errors.length > 0) {
        errors.forEach(e => toast.error(e.description));
      } else {
        toast.success('Place linked to company successfully');
        setLinkedPlaces(prev => [
          ...prev,
          {
            place_id: selectedPlace.place_id,
            name: selectedPlace.name,
            city: selectedPlace.city,
            address_line_1: selectedPlace.address_line_1,
          },
        ]);
        setSelectedPlace(null);
        setResetKey(k => k + 1);
      }
    } catch {
      toast.error('Failed to link place');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkPlace = async place => {
    const confirmed = await swal({
      title: 'Remove Link',
      text: `Remove "${place.name}" from this company?`,
      icon: 'warning',
      buttons: ['Cancel', 'Yes, Remove'],
    });

    if (!confirmed) return;

    try {
      setUnlinkingId(place.place_id);
      const { data } = await client.clientPrivate.mutate({
        mutation: updatePlaceMutation,
        variables: {
          input: { user_id: userId, place_id: place.place_id, company_id: null },
        },
      });

      const errors = data?.update_place?.error;
      if (errors && errors.length > 0) {
        errors.forEach(e => toast.error(e.description));
      } else {
        toast.success('Place unlinked successfully');
        setLinkedPlaces(prev => prev.filter(p => p.place_id !== place.place_id));
      }
    } catch {
      toast.error('Failed to unlink place');
    } finally {
      setUnlinkingId(null);
    }
  };

  const renderTableBody = () => {
    if (linkedPlaces.length === 0) {
      return (
        <tr>
          <td colSpan={2}>No places linked to this company yet.</td>
        </tr>
      );
    }

    return linkedPlaces.map(place => (
      <tr key={place.place_id}>
        <td>{place.name}</td>
        <td>{[place.address_line_1, place.city].filter(Boolean).join(', ')}</td>
        <td>
          <button
            type="button"
            className={`button is-danger is-small${
              unlinkingId === place.place_id ? ' is-loading' : ''
            }`}
            disabled={!!unlinkingId}
            onClick={() => handleUnlinkPlace(place)}
          >
            Delete
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <div>
      <div className="columns" style={{ alignItems: 'flex-end' }}>
        <div className="column is-4">
          <PlaceSearchSelect
            key={resetKey}
            onSelect={place => setSelectedPlace(place)}
          />
        </div>
        <div className="column is-3">
          <div className="field">
            <div className="control">
              <button
                type="button"
                className={`button is-primary${linking ? ' is-loading' : ''}`}
                disabled={!selectedPlace || linking}
                onClick={handleLinkPlace}
              >
                Link Place to Company
              </button>
            </div>
          </div>
        </div>
      </div>

      <table className="table is-fullwidth is-striped">
        <thead>
          <tr>
            <th>Place Name</th>
            <th>Address</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>{renderTableBody()}</tbody>
      </table>
    </div>
  );
};

export default CompanyDetails;
